import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import type { WorkspaceRouteManifestEntry } from "./gsb-app";
import { brand } from "@gsb-core/brand";
import { stripLanguagePrefix } from "./language-path";
import { useFormattingLocale } from "@gsb-core/react-multilang";
import { pikbaseLegalTitleForLocale } from "@gsb-core/feature-legal";

const unavailablePublicPaths = new Set([
  "/features/api",
  "/features/authorization",
  "/features/database-automation",
  "/features/query-system",
  "/faq",
  "/privacy",
  "/terms",
  "/cookies",
]);

function routeMatches(pathname: string, routePath: string): boolean {
  const pathnameParts = stripLanguagePrefix(pathname)
    .split("/")
    .filter(Boolean);
  const routeParts = routePath.split("/").filter(Boolean);
  return (
    pathnameParts.length === routeParts.length &&
    routeParts.every(
      (part, index) => part.startsWith(":") || part === pathnameParts[index],
    )
  );
}

export function getPublicRouteMetadata(
  pathname: string,
  manifest: readonly WorkspaceRouteManifestEntry[],
  locale = "en-US",
) {
  const route = manifest.find(
    (entry) => entry.shell === "public" && routeMatches(pathname, entry.path),
  );
  const title =
    (route && pikbaseLegalTitleForLocale(route.path, locale)) ??
    route?.identity ?? "Page not found";
  const shouldIndex =
    route !== undefined &&
    !unavailablePublicPaths.has(route.path) &&
    route.path === "/";
  return {
    title:
      title === brand.workspaceName
        ? title
        : `${title} · ${brand.workspaceName}`,
    description:
      route?.path === "/"
        ? `${brand.workspaceName} public product information.`
        : `${brand.workspaceName} ${title.toLowerCase()}.`,
    robots: shouldIndex ? "index,follow" : "noindex,nofollow",
    canonical: route ? `${brand.urls.workspace}${route.path}` : undefined,
  };
}

export function PublicRouteMetadata({
  manifest,
}: {
  manifest: readonly WorkspaceRouteManifestEntry[];
}) {
  const { pathname } = useLocation();
  const locale = useFormattingLocale() ?? "en-US";
  const metadata = getPublicRouteMetadata(pathname, manifest, locale);
  return (
    <Helmet>
      <title>{metadata.title}</title>
      <meta name="description" content={metadata.description} />
      <meta name="robots" content={metadata.robots} />
      {metadata.canonical ? (
        <meta property="og:url" content={metadata.canonical} />
      ) : null}
      {metadata.canonical ? (
        <link rel="canonical" href={metadata.canonical} />
      ) : null}
    </Helmet>
  );
}
